import { useEffect, useState } from "react";
import { api, formatApiError, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Card, Modal, Field, inputClass, PrimaryButton, EmptyState } from "../components/common";
import { toast } from "sonner";
import { ClipboardText, Plus, Trash, PencilSimple, Tag, FileXls, Paperclip } from "@phosphor-icons/react";

const TEMPLATES = [
  { name: "Seguridad", area: "Seguridad", findings: "1. Uso de EPP\n2. Señalización\n3. Andamios y arneses\n4. Extintores\n5. Rutas de evacuación" },
  { name: "Calidad", area: "Calidad", findings: "1. Cumplimiento de especificaciones\n2. Acabados\n3. Tolerancias\n4. Materiales certificados" },
  { name: "Financiera", area: "Financiera", findings: "1. Estimaciones vs contrato\n2. Amortización de anticipo\n3. Fondo de garantía\n4. Facturas" },
];

const empty = { title: "", description: "", area: "", responsible: "", audit_date: "", status: "abierta", tagsStr: "", findings: "" };
const STATUS = {
  abierta: { label: "Abierta", cls: "bg-amber-50 text-amber-700" },
  en_proceso: { label: "En proceso", cls: "bg-blue-50 text-blue-700" },
  cerrada: { label: "Cerrada", cls: "bg-emerald-50 text-emerald-700" },
};

export default function Audits() {
  const { user } = useAuth();
  const canDelete = ["admin", "gerente_general"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [reports, setReports] = useState([]);
  const [repForm, setRepForm] = useState({ title: "", specialty: "", author: "", notes: "", file: null });
  const [repOpen, setRepOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(empty);
  const [docFiles, setDocFiles] = useState([]);
  const [tagFilter, setTagFilter] = useState("");

  const load = () => api.get("/audits").then((r) => setItems(r.data)).catch(() => {});
  const loadReports = () => api.get("/audits/reports").then((r) => setReports(r.data)).catch(() => {});
  useEffect(() => { load(); loadReports(); }, []);

  const deleteAll = async () => {
    if (!window.confirm("¿Eliminar TODAS las auditorías? Quedarán registradas en Eliminados.")) return;
    try { await api.delete("/audits"); toast.success("Auditorías eliminadas"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const uploadReport = async (e) => {
    e.preventDefault();
    if (!repForm.file) return toast.error("Selecciona el archivo del informe");
    setBusy(true);
    const fd = new FormData();
    fd.append("title", repForm.title); fd.append("specialty", repForm.specialty);
    fd.append("author", repForm.author); fd.append("notes", repForm.notes); fd.append("file", repForm.file);
    try {
      await api.post("/audits/reports", fd);
      toast.success("Informe subido"); setRepOpen(false); setRepForm({ title: "", specialty: "", author: "", notes: "", file: null }); loadReports();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const removeReport = async (id) => {
    if (!window.confirm("¿Eliminar este informe?")) return;
    try { await api.delete(`/audits/reports/${id}`); loadReports(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const allTags = [...new Set(items.flatMap((a) => a.tags || []))];
  const shown = items.filter((a) => !tagFilter || (a.tags || []).includes(tagFilter));

  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    const payload = {
      title: form.title, description: form.description, area: form.area, responsible: form.responsible,
      audit_date: form.audit_date || null, status: form.status, findings: form.findings,
      tags: form.tagsStr.split(",").map((t) => t.trim()).filter(Boolean),
    };
    try {
      let aid = editId;
      if (editId) await api.put(`/audits/${editId}`, payload);
      else { const res = await api.post("/audits", payload); aid = res.data.id; }
      if (docFiles.length && aid) {
        for (const f of docFiles) {
          const fd = new FormData(); fd.append("file", f);
          try { await api.post(`/audits/${aid}/attachment`, fd); } catch (e) { /* continue */ }
        }
      }
      toast.success(editId ? "Auditoría actualizada" : "Auditoría registrada");
      setOpen(false); setForm(empty); setEditId(null); setDocFiles([]); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const openEdit = (a) => {
    setEditId(a.id); setDocFiles([]);
    setForm({ title: a.title, description: a.description || "", area: a.area || "", responsible: a.responsible || "", audit_date: a.audit_date || "", status: a.status || "abierta", tagsStr: (a.tags || []).join(", "), findings: a.findings || "" });
    setOpen(true);
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar auditoría? Quedará registrada en Eliminados.")) return;
    try { await api.delete(`/audits/${id}`); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const uploadAtt = async (aid, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/audits/${aid}/attachment`, fd); toast.success("Evidencia adjuntada"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader overline="Control y cumplimiento" title="Auditorías externas"
        action={<div className="flex gap-2">
          {items.length > 0 && <button onClick={() => window.open(`${api.defaults.baseURL}/audits/excel`, "_blank")} data-testid="audits-excel-btn" className="inline-flex items-center gap-2 h-11 px-4 border border-input rounded-sm font-medium text-sm hover:bg-muted transition-colors"><FileXls size={18} /> Excel</button>}
          {canDelete && items.length > 0 && <button onClick={deleteAll} data-testid="delete-all-audits-btn" className="inline-flex items-center gap-2 h-11 px-4 border border-danger/40 text-danger rounded-sm font-medium text-sm hover:bg-red-50 transition-colors"><Trash size={18} /> Eliminar todas</button>}
          <button onClick={() => setRepOpen(true)} data-testid="add-report-btn" className="inline-flex items-center gap-2 h-11 px-4 border border-input rounded-sm font-medium text-sm hover:bg-muted transition-colors"><Paperclip size={18} /> Subir informe</button>
          <PrimaryButton onClick={() => { setForm(empty); setEditId(null); setDocFiles([]); setOpen(true); }} data-testid="add-audit-btn"><Plus size={18} weight="bold" /> Nueva auditoría</PrimaryButton>
        </div>} />

      {allTags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-5">
          <button onClick={() => setTagFilter("")} className={`px-3 py-1 rounded-full text-xs font-semibold ${!tagFilter ? "bg-primary text-white" : "bg-muted text-muted-foreground"}`}>Todas</button>
          {allTags.map((t) => (
            <button key={t} onClick={() => setTagFilter(t)} data-testid={`tag-filter-${t}`} className={`px-3 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1 ${tagFilter === t ? "bg-primary text-white" : "bg-muted text-muted-foreground"}`}><Tag size={12} />{t}</button>
          ))}
        </div>
      )}

      {shown.length === 0 ? (
        <EmptyState icon={ClipboardText} title="Sin auditorías" subtitle="Registra las auditorías realizadas (calidad, seguridad, financiera…) y etiquétalas para encontrarlas rápido." />
      ) : (
        <div className="space-y-3">
          {shown.map((a) => (
            <Card key={a.id} data-testid={`audit-${a.id}`} className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-heading font-bold text-lg">{a.title}</span>
                    <span className={`px-2 py-0.5 rounded-sm text-xs font-semibold ${STATUS[a.status]?.cls || ""}`}>{STATUS[a.status]?.label || a.status}</span>
                  </div>
                  <div className="text-sm text-muted-foreground mt-0.5">{[a.area, a.responsible, a.audit_date].filter(Boolean).join(" · ")}</div>
                  {a.description && <p className="text-sm mt-2">{a.description}</p>}
                  {a.findings && <p className="text-sm mt-1"><b>Hallazgos:</b> {a.findings}</p>}
                  {(a.tags || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {a.tags.map((t) => <span key={t} className="px-2 py-0.5 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 inline-flex items-center gap-1"><Tag size={11} />{t}</span>)}
                    </div>
                  )}
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    {(a.attachments || []).map((f, i) => <a key={i} href={fileUrl(f.path)} target="_blank" rel="noreferrer" data-testid={`audit-att-view-${a.id}-${i}`} className="text-xs font-semibold text-blue-600 hover:underline inline-flex items-center gap-1"><Paperclip size={12} />{f.name}</a>)}
                    <label className="text-xs font-semibold text-primary cursor-pointer hover:underline inline-flex items-center gap-1"><Paperclip size={12} /> Adjuntar evidencia<input type="file" className="hidden" data-testid={`audit-att-${a.id}`} onChange={(e) => uploadAtt(a.id, e.target.files[0])} /></label>
                  </div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => openEdit(a)} data-testid={`edit-audit-${a.id}`} className="p-1.5 text-muted-foreground hover:text-primary"><PencilSimple size={18} /></button>
                  {canDelete && <button onClick={() => remove(a.id)} data-testid={`delete-audit-${a.id}`} className="p-1.5 text-muted-foreground hover:text-danger"><Trash size={18} /></button>}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <div className="mt-10">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="overline flex items-center gap-2"><Paperclip size={16} weight="bold" /> Informes externos</div>
            <h3 className="font-heading font-bold text-lg">Informes de terceros / especialistas</h3>
            <p className="text-sm text-muted-foreground">Sube y almacena auditorías o informes realizados por terceros (estructural, eléctrico, seguridad, etc.).</p>
          </div>
        </div>
        {reports.length === 0 ? (
          <Card className="p-6 text-sm text-muted-foreground">Aún no hay informes de terceros. Usa "Subir informe" para almacenar reportes externos por especialidad.</Card>
        ) : (
          <div className="space-y-2">
            {reports.map((r) => (
              <Card key={r.id} data-testid={`report-${r.id}`} className="p-4 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">{r.title}</span>
                    {r.specialty && <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700">{r.specialty}</span>}
                  </div>
                  <div className="text-sm text-muted-foreground mt-0.5">{[r.author && `Por ${r.author}`, new Date(r.created_at).toLocaleDateString("es-MX"), `Subido por ${r.uploaded_by}`].filter(Boolean).join(" · ")}</div>
                  {r.notes && <p className="text-sm mt-1">{r.notes}</p>}
                  <a href={fileUrl(r.file?.path)} target="_blank" rel="noreferrer" data-testid={`report-file-${r.id}`} className="text-xs font-semibold text-blue-600 hover:underline inline-flex items-center gap-1 mt-1"><Paperclip size={12} />{r.file?.name || "Ver informe"}</a>
                </div>
                {canDelete && <button onClick={() => removeReport(r.id)} data-testid={`delete-report-${r.id}`} className="p-1.5 text-muted-foreground hover:text-danger flex-shrink-0"><Trash size={18} /></button>}
              </Card>
            ))}
          </div>
        )}
      </div>

      <Modal open={repOpen} onClose={() => setRepOpen(false)} title="Subir informe de tercero">
        <form onSubmit={uploadReport} className="space-y-4">
          <Field label="Título *"><input required data-testid="report-title" className={inputClass} value={repForm.title} onChange={(e) => setRepForm({ ...repForm, title: e.target.value })} placeholder="Ej. Dictamen estructural — Torre A" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Especialidad"><input data-testid="report-specialty" className={inputClass} value={repForm.specialty} onChange={(e) => setRepForm({ ...repForm, specialty: e.target.value })} placeholder="Estructural, Eléctrico…" /></Field>
            <Field label="Realizado por (tercero)"><input data-testid="report-author" className={inputClass} value={repForm.author} onChange={(e) => setRepForm({ ...repForm, author: e.target.value })} placeholder="Nombre / despacho" /></Field>
          </div>
          <Field label="Notas"><textarea className={inputClass} rows={2} value={repForm.notes} onChange={(e) => setRepForm({ ...repForm, notes: e.target.value })} /></Field>
          <Field label="Archivo del informe *"><input required type="file" data-testid="report-file-input" onChange={(e) => setRepForm({ ...repForm, file: e.target.files[0] })} className="w-full text-sm border border-dashed border-input rounded-sm p-3 file:mr-3 file:px-3 file:py-1.5 file:border-0 file:bg-primary file:text-white file:rounded-sm file:font-medium" /></Field>
          <PrimaryButton type="submit" disabled={busy} data-testid="save-report-btn">{busy ? "Subiendo…" : "Subir informe"}</PrimaryButton>
        </form>
      </Modal>

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Editar auditoría" : "Nueva auditoría"}>
        <form onSubmit={save} className="space-y-4">
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs text-muted-foreground">Plantilla:</span>
            {TEMPLATES.map((t) => <button type="button" key={t.name} data-testid={`tpl-${t.name}`} onClick={() => setForm({ ...form, area: t.area, findings: t.findings })} className="px-2.5 py-1 rounded-full text-xs font-semibold bg-muted hover:bg-primary hover:text-white transition-colors">{t.name}</button>)}
          </div>
          <Field label="Título *"><input required data-testid="audit-title" className={inputClass} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ej. Auditoría de seguridad — Torre A" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Área / tipo"><input className={inputClass} value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} placeholder="Seguridad, Calidad, Financiera…" /></Field>
            <Field label="Responsable"><input className={inputClass} value={form.responsible} onChange={(e) => setForm({ ...form, responsible: e.target.value })} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Fecha"><input type="date" className={inputClass} value={form.audit_date} onChange={(e) => setForm({ ...form, audit_date: e.target.value })} /></Field>
            <Field label="Estado"><select className={inputClass} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}><option value="abierta">Abierta</option><option value="en_proceso">En proceso</option><option value="cerrada">Cerrada</option></select></Field>
          </div>
          <Field label="Descripción"><textarea className={inputClass} rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
          <Field label="Hallazgos / resultados"><textarea className={inputClass} rows={2} value={form.findings} onChange={(e) => setForm({ ...form, findings: e.target.value })} /></Field>
          <Field label="Etiquetas (separadas por coma)"><input data-testid="audit-tags" className={inputClass} value={form.tagsStr} onChange={(e) => setForm({ ...form, tagsStr: e.target.value })} placeholder="Ej. seguridad, 2026, torre-a" /></Field>
          <Field label="Documentos (puedes seleccionar varios)">
            <input type="file" multiple data-testid="audit-docs-input" onChange={(e) => setDocFiles(Array.from(e.target.files))} className="w-full text-sm border border-dashed border-input rounded-sm p-3 file:mr-3 file:px-3 file:py-1.5 file:border-0 file:bg-primary file:text-white file:rounded-sm file:font-medium" />
            {docFiles.length > 0 && <div className="text-xs text-muted-foreground mt-1">{docFiles.length} archivo(s) seleccionado(s): {docFiles.map((f) => f.name).join(", ")}</div>}
            {editId && <div className="text-xs text-muted-foreground mt-1">Se agregarán a los documentos existentes de esta auditoría.</div>}
          </Field>
          <PrimaryButton type="submit" disabled={busy} data-testid="save-audit-btn">{busy ? "Guardando…" : (editId ? "Guardar cambios" : "Registrar auditoría")}</PrimaryButton>
        </form>
      </Modal>
    </div>
  );
}

